import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { DeallocateButton } from 'src/components/Buttons/DeallocateButton';
import { RejectButton } from 'src/components/Buttons/RejectButton';
import { ReloadButton } from 'src/components/Buttons/ReloadButton';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { ConfirmDialog } from 'src/core/interim/dialogs/ConfirmDialog';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { vars } from 'src/core/themes';
import { Button, DropdownMenu, Stack } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { useGenerateGroupInvoicePdf } from 'src/hooks/api/useGenerateGroupInvoicePdf';
import { SALES_GROUP_QUERY_KEY } from 'src/hooks/api/useGetSalesGroup';
import { useRefundToCredit } from 'src/hooks/api/useRefundToCredit';
import { useSalesGroupBulkActions } from 'src/hooks/api/useSalesGroupBulkActions';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserCanUpdateSale } from 'src/hooks/useUserHasSalePermissions';
import { ModalFooter } from 'src/modals/Modal/Modal.styled';
import * as saleDetailsStyles from 'src/modals/SaleDetails/SaleDetails.css';
import { DownloadIcon, ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { SALE_CANCELLATION_REASON_CID } from 'src/utils/constants/contentIdMaps';
import {
  MarketplacePaymentState,
  Permission,
  Sale,
  SaleActionType,
  SaleCancellationReason,
  SalesGroupDetails,
} from 'src/WebApiController';

/**
 * Bulk Reject / Cancel(Deallocate) footer for the Sales Group modal (POS-8632).
 * Reuses the exact single-sale controls (RejectButton / DeallocateButton +
 * ConfirmDialog + PosEnumSelect cancellation-reason picker + admin-hold Checkbox)
 * and acts as a bulk over the group's eligible member sales via useSalesGroupBulkActions,
 * which loops the per-sale SaleClient endpoints. Mirrors SaleDetailsFooter's layout:
 * <ModalFooter> wrapping the right-aligned styles.actionsContainer row.
 */
export function SalesGroupModalFooter({
  salesGroupId,
  sales,
  group,
}: {
  salesGroupId: string;
  sales: Sale[];
  group: SalesGroupDetails;
}) {
  const { loginContext } = useAppContext();
  const queryClient = useQueryClient();
  const { rejectSales, deallocateSales } =
    useSalesGroupBulkActions(salesGroupId);
  const refundToCredit = useRefundToCredit();
  const [isRefunding, setIsRefunding] = useState(false);

  // POS-8856: shared group-invoice generation. React Query dedupes the underlying
  // getSalesGroup / getCatalogSlim fetches with the modal's existing queries.
  const { generateBlob, isPreparing } =
    useGenerateGroupInvoicePdf(salesGroupId);
  const [isDownloading, setIsDownloading] = useState(false);

  // POS-8856: build the GROUP invoice PDF and DOWNLOAD it as a file (mirrors
  // generateAndDownloadGroupPDF in LaunchDownloadRetailSaleInvoice). Null blob → no-op.
  const onPrintInvoice = useCallback(async () => {
    setIsDownloading(true);
    try {
      const blob = await generateBlob();
      if (!blob) {
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RetailGroupInvoice_${salesGroupId}_${format(
        new Date(),
        'yyyy-MM-dd'
      )}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloading(false);
    }
  }, [generateBlob, salesGroupId]);

  const printInvoiceDisabled =
    isPreparing || isDownloading || sales.length === 0;
  const showPrintProcessing = isPreparing || isDownloading;

  // POS-8632: refetch the group payload so the modal reflects the latest state
  // after out-of-band changes (payments, per-sale edits made elsewhere, etc.).
  const refreshGroup = () =>
    queryClient.invalidateQueries({
      queryKey: [SALES_GROUP_QUERY_KEY, salesGroupId],
    });

  // Gate writes on a representative member sale (mirrors the modal's existing
  // sections). Must be called unconditionally.
  const canUpdate = useUserCanUpdateSale(sales?.[0], false /* isBulkEdit */);

  const hasAdminPermission = useUserHasAnyOfPermissions(
    Permission.SellerAccount_Admin,
    Permission.Inventory_ManageAdminHolds
  );

  const rejectEligible = useMemo(
    () =>
      sales.filter(
        (s) =>
          s.cancellationReason == null &&
          s.actions?.includes(SaleActionType.Reject)
      ),
    [sales]
  );

  const deallocateEligible = useMemo(
    () =>
      sales.filter(
        (s) =>
          s.cancellationReason == null &&
          (s.actions?.includes(SaleActionType.Deallocate) ||
            s.actions?.includes(SaleActionType.RejectAllocation))
      ),
    [sales]
  );

  // Two independent confirm dialogs, one per action, each spanning the whole group.
  const {
    launchDialog: launchRejectDialog,
    closeDialog: closeRejectDialog,
    dialogProps: rejectDialogProps,
  } = useBasicDialog();
  const {
    launchDialog: launchDeallocateDialog,
    closeDialog: closeDeallocateDialog,
    dialogProps: deallocateDialogProps,
  } = useBasicDialog();

  const [cancellationReason, setCancellationReason] =
    useState<SaleCancellationReason | null>(null);
  const [shouldAdminHold, setShouldAdminHold] = useState<boolean>(
    !!loginContext?.user?.activeAccount?.sellerAccountSettings
      ?.adminHoldIndefinitelyForCancelledSales
  );

  const showReject = canUpdate && rejectEligible.length > 0;
  const showDeallocate = canUpdate && deallocateEligible.length > 0;

  // POS-8726: Group-level refund button. Visible when the group has Paid payment
  // lines (the group is paid), hidden when all lines are already reversed/pending.
  const paymentLines = group.aggregatedPaymentLines ?? [];
  const hasPaidPaymentLines = paymentLines.some(
    (l) => l.state === MarketplacePaymentState.Paid
  );
  const allLinesRefunded =
    paymentLines.length > 0 &&
    paymentLines.every(
      (l) =>
        l.state === MarketplacePaymentState.Reversed ||
        l.state === MarketplacePaymentState.PendingReversal
    );
  const showRefund = hasPaidPaymentLines && !allLinesRefunded;

  // POS-8726: Refund each member sale in the group to buyer credit.
  // Send amount=0 to let the backend compute the correct per-sale refund amount
  // from the actual (non-aggregated) payment lines. The frontend's aggregatedPaymentLines
  // merge lines across sales, making per-sale amount computation unreliable.
  const handleRefundGroup = useCallback(async () => {
    const memberSales = group.sales ?? [];
    if (memberSales.length === 0) return;

    setIsRefunding(true);
    try {
      for (const sale of memberSales) {
        await refundToCredit.mutateAsync({
          saleId: sale.id,
          amount: 0, // backend computes from sale's Paid lines
          notes: null,
        });
      }
      await queryClient.invalidateQueries({
        queryKey: [SALES_GROUP_QUERY_KEY, salesGroupId],
      });
    } finally {
      setIsRefunding(false);
    }
  }, [
    group.sales,
    group.aggregatedPaymentLines,
    refundToCredit,
    queryClient,
    salesGroupId,
  ]);

  return (
    <ModalFooter>
      <div className={saleDetailsStyles.actionsContainer}>
        {/* POS-8726: Group-level Refund dropdown — left side of footer */}
        {showRefund && (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild disabled={isRefunding}>
              <Button variant="outline" disabled={isRefunding}>
                <Content id={ContentId.Refund} />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="start">
                <DropdownMenu.Item onSelect={handleRefundGroup}>
                  <Content id={ContentId.RefundToCredit} />
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        )}
        {/*
          POS-8856: Download the full multi-event GROUP invoice PDF as a file.
          Mirrors LaunchDownloadRetailSaleInvoice's ButtonWithIcon + RotatingWrapper
          processing pattern; outline variant so it matches the footer's other buttons.
        */}
        <ButtonWithIcon
          variant="outline"
          disabled={printInvoiceDisabled}
          textContentId={ContentId.PrintInvoice}
          titleContentId={ContentId.PrintInvoice}
          onClick={printInvoiceDisabled ? undefined : onPrintInvoice}
          icon={
            showPrintProcessing ? (
              <RotatingWrapper>
                <ProcessingIcon fill={vars.color.textBrand} />
              </RotatingWrapper>
            ) : (
              <DownloadIcon
                withHoverEffect
                disabled={printInvoiceDisabled}
                fill={vars.color.textBrand}
                size={vars.iconSize.m}
              />
            )
          }
        />
        <ReloadButton
          variant="outline"
          textContentId={ContentId.Refresh}
          titleContentId={ContentId.Refresh}
          disabled={rejectSales.isPending || deallocateSales.isPending}
          onClick={refreshGroup}
        />
        {showReject && (
          <div>
            <RejectButton
              variant="outline"
              disabled={rejectSales.isPending}
              textContentId={ContentId.RejectSale}
              titleContentId={ContentId.RejectSale}
              onClick={() => {
                setCancellationReason(null);
                launchRejectDialog();
              }}
            />
            <ConfirmDialog
              {...rejectDialogProps}
              disabled={rejectSales.isPending}
              isLoading={rejectSales.isPending}
              headerText={<Content id={ContentId.RejectSale} />}
              onOkay={() => {
                rejectSales.mutate({
                  saleIds: rejectEligible.map((s) => s.id),
                  cancellationReason,
                });
                closeRejectDialog();
              }}
              bodyText={
                <Stack direction="column" gap="l">
                  <Stack
                    direction="column"
                    gap="s"
                    className={saleDetailsStyles.cancellationReasonWrapper}
                  >
                    <Content id={ContentId.CancellationReason} />
                    <PosEnumSelect
                      className={saleDetailsStyles.cancellationReasonSelector}
                      valueOptionsContent={SALE_CANCELLATION_REASON_CID}
                      value={cancellationReason}
                      onChange={setCancellationReason}
                    />
                  </Stack>
                  <Content id={ContentId.RejectSaleWarning} />
                </Stack>
              }
            />
          </div>
        )}
        {showDeallocate && (
          <div>
            {/*
              POS-8632: We deliberately do NOT replicate LaunchDeallocateSale's
              Offline+Fulfilled status-flip pre-step here. We rely on actions-based
              eligibility + Promise.allSettled to surface any members that fail.
            */}
            <DeallocateButton
              variant="outline"
              disabled={deallocateSales.isPending}
              textContentId={ContentId.DeallocateSale}
              titleContentId={ContentId.DeallocateSale}
              onClick={() => launchDeallocateDialog()}
            />
            <ConfirmDialog
              {...deallocateDialogProps}
              disabled={deallocateSales.isPending}
              isLoading={deallocateSales.isPending}
              headerText={<Content id={ContentId.DeallocateSale} />}
              onOkay={() => {
                deallocateSales.mutate({
                  saleIds: deallocateEligible.map((s) => s.id),
                  adminHold: shouldAdminHold,
                });
                closeDeallocateDialog();
              }}
              bodyText={
                <Stack direction="column" gap="l">
                  <Content id={ContentId.DeallocateSaleWarning} />
                  {hasAdminPermission && (
                    <Checkbox
                      labelPosition="right"
                      checked={shouldAdminHold}
                      onChange={() => setShouldAdminHold((p) => !p)}
                      label={<Content id={ContentId.PutSaleListingOnHold} />}
                    />
                  )}
                </Stack>
              }
            />
          </div>
        )}
      </div>
    </ModalFooter>
  );
}
